import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { reassignDeviceMutation, rescuePetsQuery } from '../../lib/rescueQueries';
import type { RescuePetsData } from '../../lib/rescueQueries';
import { useAssignDevice } from '../../hooks/useAssignDevice';
import { usePreAssignDeviceCheck } from '../../hooks/usePreAssignDeviceCheck';
import styles from './Pets.module.scss';
import assignStyles from './AssignDeviceToPetModal.module.scss';

interface AssignDeviceModalProps {
  open: boolean;
  onClose: () => void;
  initialPetId?: string;
}

export default function AssignDeviceModal({ open, onClose, initialPetId }: AssignDeviceModalProps) {
  const { showToast } = useToast();
  const [moduleId, setModuleId] = useState('');
  const [petId, setPetId] = useState('');
  const [moduleError, setModuleError] = useState('');
  const [reassignTarget, setReassignTarget] = useState<{ moduleId: string; petId: string; petName: string } | null>(
    null,
  );
  // The serial and pet the user was told the collar isn't on their books for; assigning adds it to them.
  const [addTarget, setAddTarget] = useState<{ moduleId: string; petId: string } | null>(null);

  const { data: petsData, loading: petsLoading } = useQuery<RescuePetsData>(rescuePetsQuery);

  const pets = useMemo(() => {
    if (!petsData) {
      return [];
    }
    const rescueHousehold = petsData.currentUser?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');
    const allPets = rescueHousehold?.household.pets ?? [];
    return allPets.filter((pet) => !pet.device);
  }, [petsData]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (initialPetId && pets.some((p) => p.id === initialPetId)) {
      setPetId(initialPetId);
    } else if (pets.length > 0 && !petId) {
      setPetId(pets[0].id);
    }
  }, [open, initialPetId, pets]);

  const { assign, assigning } = useAssignDevice();
  const [reassignDevice, { loading: reassigning }] = useMutation(reassignDeviceMutation, {
    refetchQueries: 'active' as const,
  });
  const { check: preAssignCheck, checking } = usePreAssignDeviceCheck();

  const busy = assigning || reassigning || checking;
  const moduleIdNormalized = moduleId.trim().toUpperCase();
  const selectedPet = pets.find((p) => p.id === petId);
  const reassignActive =
    !!reassignTarget && reassignTarget.moduleId === moduleIdNormalized && reassignTarget.petId === petId;
  const addActive = !!addTarget && addTarget.moduleId === moduleIdNormalized && addTarget.petId === petId;

  function handleClose() {
    setModuleId('');
    setPetId('');
    setModuleError('');
    setReassignTarget(null);
    setAddTarget(null);
    onClose();
  }

  async function handleActivate() {
    // Re-entrancy guard: the device input's Enter handler fires regardless of
    // button disabled state, so a habitual double-tap would otherwise confirm
    // a pending reassign before the user has read the notice.
    if (busy) {
      return;
    }
    if (!moduleIdNormalized) {
      setModuleError('Device ID is required');
      return;
    }
    if (!petId || !selectedPet) {
      return;
    }
    const confirmedReassign = reassignActive;
    const confirmedAdd = addActive;
    setModuleError('');

    try {
      if (!confirmedReassign && !confirmedAdd) {
        const verdict = await preAssignCheck({ moduleId: moduleIdNormalized, currentPetId: petId });
        if (verdict.type === 'blocked') {
          setModuleError(verdict.message);
          return;
        }
        if (verdict.type === 'reassign') {
          setReassignTarget({ moduleId: moduleIdNormalized, petId, petName: verdict.petName });
          return;
        }
        if (verdict.type === 'addToInventory') {
          setAddTarget({ moduleId: moduleIdNormalized, petId });
          return;
        }
      }

      if (confirmedReassign) {
        await reassignDevice({ variables: { moduleId: moduleIdNormalized, toPetId: petId } });
      } else {
        await assign({ moduleId: moduleIdNormalized, petId, addToInventory: confirmedAdd });
      }
      showToast(`Device ${moduleIdNormalized} assigned to ${selectedPet.name}`);
      handleClose();
    } catch (err) {
      // The confirmation no longer holds, so the next submit runs the checks again.
      setReassignTarget(null);
      setAddTarget(null);
      setModuleError(err instanceof Error ? err.message : 'Failed to activate device');
    }
  }

  const canSubmit = moduleIdNormalized.length > 0 && petId.length > 0 && !busy;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle>Assign Fi Device</ModalTitle>
      <p className={styles.modalDescription}>Enter the device&rsquo;s module ID and select a pet to assign it to.</p>

      <PortalField label="Device ID" error={moduleError} showError={!!moduleError}>
        <PortalInput
          value={moduleId}
          onChange={(e) => {
            setModuleId(e.target.value);
            setModuleError('');
            setReassignTarget(null);
            setAddTarget(null);
          }}
          onKeyDown={(e) => e.key === 'Enter' && handleActivate()}
          placeholder="e.g. M1XXXXXXXX"
          hasError={!!moduleError}
          autoFocus
        />
      </PortalField>

      <PortalField label="Pet">
        <PortalSelect
          value={petId}
          onChange={(e) => {
            setPetId(e.target.value);
            setReassignTarget(null);
            setAddTarget(null);
          }}
          disabled={petsLoading}
        >
          {pets.map((pet) => (
            <option key={pet.id} value={pet.id}>
              {pet.name}
            </option>
          ))}
        </PortalSelect>
      </PortalField>

      {reassignActive && selectedPet && (
        <div className={assignStyles.reassignNotice}>
          Device <strong>{reassignTarget!.moduleId}</strong> is currently attached to{' '}
          <strong>{reassignTarget!.petName}</strong>. Continuing will remove it from{' '}
          <strong>{reassignTarget!.petName}</strong> and assign it to <strong>{selectedPet.name}</strong>.
        </div>
      )}

      {addActive && selectedPet && (
        <div className={assignStyles.reassignNotice}>
          Device <strong>{moduleIdNormalized}</strong> isn&rsquo;t in your inventory yet. Assigning it to{' '}
          <strong>{selectedPet.name}</strong> also adds it to your device inventory.
        </div>
      )}

      <ModalActions>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleActivate} disabled={!canSubmit}>
          {checking
            ? 'Checking device...'
            : reassigning
              ? 'Reassigning...'
              : assigning
                ? 'Activating...'
                : reassignActive
                  ? 'Reassign Device'
                  : addActive
                    ? 'Add and Activate Device'
                    : 'Activate Device'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
